import React from 'react';
import { useAppSelector } from '../../../store';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { useResolvePublishedShiftPlanV2 } from '../../../hooks/useResolvePublishedShiftPlanV2';
import CurrentPlanSummaryContainer from './CurrentPlanSummaryContainer';
import CurrentPlanSummaryV2Container from './v2/CurrentPlanSummaryContainer';
import { SPMSGetShiftPlanResponse } from '../../../types/shiftPlan';
import { PostPublishView } from '../../../store/slices/v2/publishedShiftPlanFeatureSlice';

/**
 * Feature gate for the refactored (V2) published shift plan page (UTR-REFLEX-1163).
 *
 * Resolves the `RCC_PUBLISHED_SHIFT_PLAN_V2` flag (RnRRCCFacadeService IsFeatureEnabled)
 * and renders V2 when enabled, else V1. While unresolved (`isV2Enabled === null`) it
 * renders nothing — mounting V1 first and swapping to V2 on resolve would remount at
 * the same slot and fetch twice, so we wait and mount the right page once. The
 * unresolved window is a facade round-trip when deployed, instant locally.
 *
 * Follows the established checkout gate pattern (checkoutSlice + ShiftPlanLayout).
 */
interface CurrentPlanSummaryGatedProps {
  shiftPlans: SPMSGetShiftPlanResponse[];
  selectedTab: string;
  selectedDate: string;
  selectedShift: string;
  /** Forwarded to the V2 page: the just-published plan to open on (or null). */
  postPublishView?: PostPublishView | null;
  onNeedsTakeOver: (displayMessage: string) => void;
  onPlanSuccess: (planId: string) => void;
}

const CurrentPlanSummaryGated: React.FC<CurrentPlanSummaryGatedProps> = (props) => {
  const stationCode = useAppSelector(selectStationCode);
  const isV2Enabled = useResolvePublishedShiftPlanV2(stationCode);

  // Flag not resolved yet — render nothing so we don't mount a page we'd only
  // have to unmount once the flag lands (see the note above on avoiding remounts).
  if (isV2Enabled === null) {
    return null;
  }

  if (isV2Enabled) {
    return <CurrentPlanSummaryV2Container {...props} />;
  }

  // Flag resolved false → existing V1 page.
  return <CurrentPlanSummaryContainer {...props} />;
};

export default CurrentPlanSummaryGated;
